'use strict';

namespace Core.ManageBooking {

    type DestinationSummary = Core.Destinations.DestinationSummary;

    interface BookedTripApi {
        readonly Adults: number;
        Bookings: ProductBooking[];
        readonly Children: number;
        readonly EndDate: string;
        readonly Infants: number;
        readonly StartDate: string;
    }

    interface CancellationOptions {
        CanAutoCancel: boolean;
        RejectionReason: number;
        CancellationDetails: null | {
            RefundableTotalBeforeCharges: number;
            TotalPaidLessFees: number;
            CancellationPercentage: number | null;
            CancellationAmount: number | null;
            RefundableTotal: number;
            NonRefundableFeesPaid: number;
            ExpiryDate: string;
        };
    }

    angular
        .module('core')
        .service('bookingsManagerService', BookingsManagerService);

    export interface BookingsManagerService {
        getCurrentTripForUser(): ng.IPromise<BookedTrip|null>;
        getCancellationOptions(bookingId: number): ng.IPromise<CancellationOptions|null>;
        getTrip(bookingId: number): ng.IPromise<BookedTrip>;
    }

    function BookingsManagerService (
        $http: ng.IHttpService,
        $q: ng.IQService,
        $routeParams: ng.route.IRouteParamsService,
        dateUtil: any,
        imageService: any,
        destinationService: any
    ) : BookingsManagerService {

        const service: BookingsManagerService = {
            getCurrentTripForUser,
            getCancellationOptions,
            getTrip
        };

        enum BookingTypePropertyMap {
            FlightBooking = BookingType.Flight,
            AttractionsBooking = BookingType.AttractionTickets,
            CarHireBooking = BookingType.CarHire,
            HotelBooking = BookingType.Accommodation,
            AirportParkingBooking = BookingType.Parking,
            TransferBooking = BookingType.Transfer,
            InsuranceBooking = BookingType.TravelInsurance,
            MarhabaBooking = BookingType.Marhaba,
            BundleBooking = BookingType.Bundle,
            AdHocBooking = BookingType.AdHoc,
        }

        function getCancellationOptions(bookingId: number) {
            let url = `/api/bookings/cancellations/options?bookingId=${bookingId}`;
            return $http.get(url).then<CancellationOptions | null>(response => response && response.data);
        }

        function getCurrentTripForUser() {
            let url = `/api/bookings/trips/current?culturecode=${TRPCONFIG.Domain.CultureCode}`;
            return $http.get<BookedTripApi>(url).then<BookedTrip|null>((response) => {
                if (response.status === 204) {
                    // 'No content' - this is expected if there is no current trip!
                    return null;
                }
                return _enrichBookedTrip(response.data);
            });
        }

        function getTrip(bookingId: number): ng.IPromise<BookedTrip> {
            let url = `/api/bookings/${bookingId}/trip?culturecode=${TRPCONFIG.Domain.CultureCode}`;
            return $http.get<BookedTripApi>(url)
                .then(({data}) => {
                    if ($routeParams.ref) {
                        let refs = $routeParams.ref.toUpperCase().split('|');
                        data.Bookings = _.filter(data.Bookings, b => _.find(refs,
                            r => r === b['BookingRef'].toUpperCase()));
                    }
                    return _enrichBookedTrip(data);
                });
        }

        function _enrichBookedTrip(booking: BookedTripApi): ng.IPromise<BookedTrip> {
            // Decorate trip with properties not returned by the API response
            let trip = new BookedTrip();
            trip.StartDate = booking.StartDate;
            trip.EndDate = booking.EndDate;
            trip.Adults = booking.Adults;
            trip.Children = booking.Children;
            trip.Infants = booking.Infants;

            const startDate: Date = dateUtil.parseDate(trip.StartDate);
            const endDate: Date = dateUtil.parseDate(trip.EndDate);
            trip.Duration = dateUtil.getDifferenceInDays(startDate, endDate);
            trip.DaysUntilDeparture = dateUtil.getDifferenceInDays(new Date(), startDate);
            trip.DaysUntilReturn = dateUtil.getDifferenceInDays(new Date(), endDate);

            //payment bits
            _.each(booking.Bookings, b => {

                //set the currency code to be the same as the first bookings cleared payment currency
                //handle cases where agents skip a payment (no payment info)
                if (!trip.PaymentDetails.CurrencyCode) {
                    const head = _.head(b.PaymentInfo.ClearedPayments);
                    trip.PaymentDetails.CurrencyCode = head ? head.ClientCurrencyCode : '';
                }

                if (TRPCONFIG.Domain2.MonthlyPaymentsEnabled) {
                    trip.PaymentDetails.Paid += _.reduce(b.PaymentInfo.ClearedPayments,
                        (r, p) => {
                            const isAmountSkipped = p.IsScheduledPayment && p.ClientCurrencyPaymentAmount < 0;

                            return isAmountSkipped ? r : r + p.ClientCurrencyPaymentAmount;
                        }, 0);
                } else {
                    trip.PaymentDetails.Paid += _.reduce(b.PaymentInfo.ClearedPayments,
                        (r, p) => r + p.ClientCurrencyPaymentAmount,
                        0);
                }

                _.each(b.PaymentInfo.FuturePayments, p => {
                    const updateArray = (arr :BalanceDue[], amount :number, due :string, currencyCode :string) => {
                        const item = _.find(arr, { 'Due' : due , 'CurrencyCode' : currencyCode});
                        if (item) {
                            item.Amount += amount;
                        } else {
                            arr.push({Amount:amount, BookingType:b.BookingType, CurrencyCode:currencyCode, Due:due});
                        }
                    };

                    trip.PaymentDetails.Total += p.ClientCurrencyPaymentAmount;
                    updateArray(
                        trip.PaymentDetails.Balances, p.ClientCurrencyPaymentAmount, p.PaymentDate, p.ClientCurrencyCode
                    );
                });

                _.each(b.PaymentInfo.DirectPayments, p => {
                    trip.PaymentDetails.PayDirect.push(
                        { Amount: p.Amount,
                          LocalAmount: p.LocalAmount,
                          LocalCurrency: p.LocalCurrency,
                          BookingType: b.BookingType
                        }
                    );
                    trip.PaymentDetails.Total += p.Amount;
                });

                delete b.PaymentInfo; //remove the payment info as we shouldn't need it going forward
            });
            trip.PaymentDetails.Total += trip.PaymentDetails.Paid; //set the total correctly
            trip.PaymentDetails.Outstanding = trip.PaymentDetails.Total - trip.PaymentDetails.Paid;
            trip.PaymentDetails.Balances = _.sortBy(trip.PaymentDetails.Balances, 'Due'); //tidy up

            //map our bookings array to our bookedTrip properties
            _mapBookingsArrayToProperties(trip, booking.Bookings);

            let mainBooking = <ProductBooking | undefined>(trip.FlightBooking || trip.HotelBooking);
            if (!mainBooking) {
                mainBooking = booking.Bookings[0];
            }
            trip.MainBookingId = mainBooking.BookingId;

            if (trip.DaysUntilDeparture >  0) {
                trip.TripStage = TripStage.PreTrip;
            } else if (trip.DaysUntilReturn >  0) {
                trip.TripStage = TripStage.OnTrip;
            } else {
                trip.TripStage = TripStage.PostTrip;
            }

            // get destination based on either hotel OR flight destination airport
            let dest: DestinationSummary|null = null;
            if (trip.HotelBooking) {
                const b = trip.HotelBooking;

                // use Province OR first destination
                const dests: DestinationSummary[] = b.Hotel.Destinations;
                dest = _.find(dests,
                        ['Type', Destinations.DestinationType.Province])
                    || dests[0];
            } else if (trip.FlightBooking) {
                // use destination of arrival airport
                dest = _.head(trip.FlightBooking.Legs).ArrivalAirport.Destination;
            } else if (trip.TransferBooking) {
                //we handle sorting out the destination info a little further down
                //look there
            } else if (trip.AttractionsBooking) {
                dest = _.head(trip.AttractionsBooking.Attractions).Destination;
            }

            let destPromise: ng.IPromise<any>;
            if (dest) {
                trip.Destination = dest;
                trip.Destination.ImageUrl= imageService.getDestinationImage({ Id: dest.Id, Type: dest.Type });
                // Make an additional API call to get the destination landing page path
                destPromise = destinationService.getDetails(angular.copy(trip.Destination), ['PagePath']);
            } else {
                destPromise = $q.when();
            }

            //extend product infos
            if (trip.HotelBooking) {
                const b = trip.HotelBooking;

                trip.HotelBooking.Duration = dateUtil.dayDiff(b.CheckInDate, b.CheckOutDate);

                // get hotel image url
                const d = TRPCONFIG.DeviceSettings.HotelAvailabilityImageSize;
                b.Hotel.ImageUrl = imageService.getMainImageUrl(b.Hotel.EstabId, d, d, true);
            }

            if (trip.FlightBooking) {
                _.each(trip.FlightBooking.Legs, l => {
                    const start = dateUtil.parseDate(l.DepartureTime);
                    const end = dateUtil.parseDate(l.ArrivalTime);
                    l.DayDiff = dateUtil.getDifferenceInDays(start, end);
                });
            }

            if (trip.TransferBooking) {
                trip.TransferBooking.Legs = [];
                if(trip.TransferBooking.Outbound) {
                    trip.TransferBooking.Legs.push(trip.TransferBooking.Outbound);
                }
                if(trip.TransferBooking.Return) {
                    trip.TransferBooking.Legs.push(trip.TransferBooking.Return);
                }
                trip.TransferBooking.Title = trip.TransferBooking.Legs[0].Title;

                //a bit of a hack to use a default image for transfers
                if (!trip.Destination) {
                    trip.Destination = {
                        Id: 0,
                        Type: Destinations.DestinationType.Universal,
                        Title: trip.TransferBooking.Legs[0].DropOffPoint,
                        ImageUrl: '/build/img/backdrops/transfers-dec16.jpg'
                    };
                }
            }

            return destPromise.then((response) => {
                if (response && trip.Destination) {
                    trip.Destination.PagePath = response.PagePath;
                }
                return trip;
            });
        }

        function _mapBookingsArrayToProperties(trip :BookedTrip, bookings :ProductBooking[]) :void {
            //go through the array setting our properties, currently we support only one of each product
            //type, this may change in future possibly
            _.each(bookings, b => {
                const property = BookingTypePropertyMap[b.BookingType];
                trip[property] = b;
            });
        }

        return service;
    }
}
